import React, { useState } from 'react';
import { Ticket, Plus, Trash2, Copy, ToggleLeft, ToggleRight } from 'lucide-react';
import { useCoupons, useDeleteCoupon, useToggleCoupon } from '@/hooks/useApi';
import { copyToClipboard, formatDate } from '@/lib/utils';
import { getErrorMessage } from '@/lib/api';
import Button from '@/components/ui/Button';
import Modal from '@/components/ui/Modal';
import Alert from '@/components/ui/Alert';
import CreateCouponModal from '@/components/CreateCouponModal';
import { PageHeader } from '@/components/ui/PageHeader';
import { Panel } from '@/components/ui/Panel';
import { Badge } from '@/components/ui/Badge';
import { EmptyState } from '@/components/ui/EmptyState';
import { Table, THead, TBody, Tr, Th, Td, TableSkeleton } from '@/components/ui/Table';

const CouponsPage: React.FC = () => {
  const { data: coupons, isLoading } = useCoupons();
  const deleteCoupon = useDeleteCoupon();
  const toggleCoupon = useToggleCoupon();

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [alertMessage, setAlertMessage] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);

  const handleCopyCode = async (code: string) => {
    const success = await copyToClipboard(code);
    setAlertMessage(
      success
        ? { type: 'success', message: `Coupon code "${code}" copied` }
        : { type: 'error', message: 'Failed to copy code' }
    );
  };

  const handleToggle = async (id: string, currentlyActive: boolean) => {
    try {
      await toggleCoupon.mutateAsync({ id, activate: !currentlyActive });
      setAlertMessage({
        type: 'success',
        message: `Coupon ${!currentlyActive ? 'activated' : 'deactivated'}`,
      });
    } catch (e) {
      setAlertMessage({ type: 'error', message: getErrorMessage(e) });
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirmId) return;
    try {
      await deleteCoupon.mutateAsync(deleteConfirmId);
      setDeleteConfirmId(null);
      setAlertMessage({ type: 'success', message: 'Coupon deleted' });
    } catch (e) {
      setDeleteConfirmId(null);
      setAlertMessage({ type: 'error', message: getErrorMessage(e) });
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Coupons and discounts"
        description="Create coupons that give credits or a bonus on the next purchase."
        actions={
          <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setCreateModalOpen(true)}>
            Create coupon
          </Button>
        }
      />

      <Panel title="Your coupons" bodyClassName="px-0 pb-2">
        {isLoading ? (
          <TableSkeleton rows={4} />
        ) : !coupons || coupons.length === 0 ? (
          <EmptyState
            icon={<Ticket />}
            title="No coupons"
            description="Create your first coupon to offer discounts."
            action={
              <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setCreateModalOpen(true)}>
                Create coupon
              </Button>
            }
          />
        ) : (
          <Table minWidth={820}>
            <THead>
              <tr>
                <Th>Code</Th>
                <Th>Description</Th>
                <Th>Gives</Th>
                <Th align="right">Usage</Th>
                <Th>Status</Th>
                <Th>Expires</Th>
                <Th align="right"><span className="sr-only">Actions</span></Th>
              </tr>
            </THead>
            <TBody>
              {coupons.map((coupon) => (
                <Tr key={coupon.id}>
                  <Td>
                    <code className="rounded-md bg-surface-raised px-2 py-0.5 font-mono text-xs text-foreground">{coupon.code}</code>
                  </Td>
                  <Td muted>{coupon.description}</Td>
                  <Td>
                    {coupon.discountType === 'percentage'
                      ? `${coupon.discountValue}% bonus on next purchase`
                      : `${coupon.discountValue.toLocaleString('en-NG')} credits`}
                  </Td>
                  <Td align="right" mono muted>
                    {coupon.usageCount} / {coupon.usageLimit}
                  </Td>
                  <Td>
                    <Badge tone={coupon.isActive ? 'success' : 'neutral'} dot>{coupon.isActive ? 'Active' : 'Inactive'}</Badge>
                  </Td>
                  <Td muted className="whitespace-nowrap">{coupon.expiresAt ? formatDate(coupon.expiresAt) : 'Never'}</Td>
                  <Td align="right">
                    <div className="flex items-center justify-end gap-1">
                      <Button size="sm" variant="ghost" onClick={() => handleCopyCode(coupon.code)} title="Copy code" aria-label="Copy code">
                        <Copy className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleToggle(coupon.id, coupon.isActive)}
                        title={coupon.isActive ? 'Deactivate' : 'Activate'}
                        aria-label={coupon.isActive ? 'Deactivate' : 'Activate'}
                      >
                        {coupon.isActive ? (
                          <ToggleRight className="h-4 w-4 text-success" />
                        ) : (
                          <ToggleLeft className="h-4 w-4 text-muted-foreground" />
                        )}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setDeleteConfirmId(coupon.id)} title="Delete" aria-label="Delete">
                        <Trash2 className="h-4 w-4 text-error" />
                      </Button>
                    </div>
                  </Td>
                </Tr>
              ))}
            </TBody>
          </Table>
        )}
      </Panel>

      {/* Create Modal */}
      <CreateCouponModal
        isOpen={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
      />

      {/* Delete Confirmation */}
      <Modal
        isOpen={!!deleteConfirmId}
        onClose={() => setDeleteConfirmId(null)}
        title="Delete coupon"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-sm text-foreground">
            Delete this coupon? This can't be undone.
          </p>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setDeleteConfirmId(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleDelete}
              loading={deleteCoupon.isLoading}
            >
              Delete coupon
            </Button>
          </div>
        </div>
      </Modal>

      {/* Alert */}
      {alertMessage && (
        <Alert
          type={alertMessage.type}
          title={alertMessage.type === 'success' ? 'Success' : 'Error'}
          message={alertMessage.message}
          isOpen={!!alertMessage}
          onClose={() => setAlertMessage(null)}
        />
      )}
    </div>
  );
};

export default CouponsPage;
